import React, { useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { useQuery } from "@apollo/react-hooks";
import gql from "fraql";
import { useLocation, useHistory } from "react-router-dom";
import get from "lodash/get";
import isEmpty from "lodash/isEmpty";
import { createCookie } from "../../utils/cookies";
import { isRegistrationClosed } from "../../utils/registrationUtil";
import LayoutPageHeader from "../LayoutPageHeader";
import SpinPageContent from "../SpinPageContent";
import RegistrationArchivedAlert from "./RegistrationArchivedAlert";
import RegistrationClosedAlert from "./RegistrationClosedAlert";
import RegistrationLoadingError from "./RegistrationLoadingError";
import RegistrationUserSubmit from "./RegistrationUserSubmit";

const GET_REG = gql`
  query RegistrationUserSubmitPage_GetReg($id: String!) {
    Reg_by_pk(id: $id) {
      id
      name
      openAt
      closeAt
      archived
    }
  }
`;

function RegistrationUserSubmitPage({ match }) {
  const { id: regId } = match.params;
  const { search } = useLocation();
  const history = useHistory();

  const queryParams = useMemo(() => new URLSearchParams(search), [search]);

  const teamIdFromQuery = useMemo(() => queryParams.get("team"), [queryParams]);

  // Store team ID from invite link in a cookie
  useEffect(() => {
    if (!isEmpty(teamIdFromQuery)) {
      const INVITE_COOKIE_EXPIRY_MS = 604800000; // 7 days
      createCookie("InvitedToTeam", teamIdFromQuery, INVITE_COOKIE_EXPIRY_MS);

      // Remove team query param from URL so it doesn't persist
      const newSearch = new URLSearchParams(search);
      newSearch.delete("team");
      const newSearchString = newSearch.toString();
      history.replace({
        pathname: match.url,
        search: newSearchString ? `?${newSearchString}` : "",
      });
    }
  }, [teamIdFromQuery, search, history, match.url]);

  const { loading: regLoading, error: regError, data: regData } = useQuery(GET_REG, {
    variables: { id: regId },
  });

  const reg = useMemo(() => get(regData, "Reg_by_pk", null), [regData]);

  function renderContent() {
    if (regLoading && !regData) {
      return <SpinPageContent style={{ height: "100%" }} />;
    }

    if (regError || !reg) {
      return <RegistrationLoadingError />;
    }

    if (isRegistrationClosed(reg.openAt, reg.closeAt)) {
      return <RegistrationClosedAlert />;
    }

    if (reg.archived) {
      return <RegistrationArchivedAlert style={{ margin: 24 }} />;
    }

    return <RegistrationUserSubmit regId={reg.id} />;
  }

  let subTitle;

  if (regLoading && !regData) {
    subTitle = <span>Loading...</span>;
  } else if (regLoading || !reg) {
    subTitle = null;
  } else {
    subTitle = reg.name;
  }

  return (
    <>
      <div
        style={{
          backgroundColor: "#fafafa",
          borderBottom: "1px solid #e8e8e8",
        }}
      >
        <div style={{ padding: "15px 24px" }}>
          <LayoutPageHeader title="Registration" subTitle={subTitle} />
        </div>
      </div>

      {renderContent()}
    </>
  );
}

RegistrationUserSubmitPage.propTypes = {
  match: PropTypes.object.isRequired,
};

export default RegistrationUserSubmitPage;
